import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Activity,
  LogIn,
  LogOut,
  Menu,
  ShieldCheck,
  UserCircle2,
  X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Header() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const links = [
    { to: "/", label: "Overview", testid: "nav-overview" },
    {
      to: "/dashboard",
      label: user ? "My Incidents" : "Dashboard",
      testid: user ? "nav-my-incidents" : "nav-dashboard",
    },
    { to: "/incidents/new", label: "New Incident", testid: "nav-new-incident" },
  ];
  if (user?.role === "admin") {
    links.push({ to: "/admin", label: "Admin", testid: "nav-admin" });
  }

  const isActive = (to) =>
    pathname === to || (to !== "/" && pathname.startsWith(to));

  const onSignOut = async () => {
    setMobileOpen(false);
    navigate("/", { replace: true });
    await logout();
    toast.success("Signed out");
  };

  return (
    <header
      className="sticky top-0 z-40 cre-glass border-b border-slate-200"
      data-testid="cre-header"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-14 flex items-center justify-between">
        {/* Logo */}
        <Link
          to="/"
          data-testid="nav-home"
          className="flex items-center gap-2 font-heading font-black text-lg tracking-tight text-slate-900"
        >
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-sky-500 via-cyan-500 to-violet-500 shadow-sm">
            <Activity className="h-4 w-4 text-white" strokeWidth={2.25} />
          </span>
          <span>CRE</span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1" data-testid="main-nav">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                isActive(l.to)
                  ? "text-slate-900 bg-slate-100"
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
              }`}
            >
              {l.label}
            </Link>
          ))}
          <span className="mx-2 h-5 w-px bg-slate-200" />
          {user === undefined ? (
            <span
              className="text-[11px] font-mono text-slate-400 px-2"
              data-testid="nav-auth-loading"
            >
              …
            </span>
          ) : user ? (
            <div className="flex items-center gap-2" data-testid="nav-user-menu">
              <span className="inline-flex items-center gap-1.5 text-xs text-slate-700 px-2 py-1 border border-slate-200 rounded-md bg-white">
                {user.role === "admin" ? (
                  <ShieldCheck
                    className="h-3.5 w-3.5 text-amber-600"
                    strokeWidth={2}
                  />
                ) : (
                  <UserCircle2
                    className="h-3.5 w-3.5 text-sky-600"
                    strokeWidth={2}
                  />
                )}
                <span
                  className="font-mono max-w-[160px] truncate"
                  data-testid="nav-user-email"
                >
                  {user.email}
                </span>
              </span>
              <button
                type="button"
                onClick={onSignOut}
                data-testid="nav-signout"
                className="text-xs px-2.5 py-1.5 border border-slate-200 text-slate-600 hover:text-slate-900 hover:border-slate-300 rounded-md transition-colors flex items-center gap-1"
              >
                <LogOut className="h-3 w-3" strokeWidth={2} />
                Sign out
              </button>
            </div>
          ) : (
            <Link
              to="/login"
              data-testid="nav-signin"
              className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 cre-cta-primary rounded-md font-semibold"
            >
              <LogIn className="h-3.5 w-3.5" strokeWidth={2} />
              Sign in
            </Link>
          )}
        </nav>

        {/* Mobile burger + compact sign-in (when logged out) */}
        <div className="md:hidden flex items-center gap-2">
          {user === null ? (
            <Link
              to="/login"
              data-testid="nav-signin-mobile"
              className="text-xs px-2.5 py-1.5 cre-cta-primary rounded-md font-semibold inline-flex items-center gap-1"
            >
              <LogIn className="h-3 w-3" strokeWidth={2} />
              Sign in
            </Link>
          ) : null}
          <button
            type="button"
            aria-label="Open menu"
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            className="p-2 -mr-2 rounded-md text-slate-700 hover:bg-slate-100"
          >
            {mobileOpen ? (
              <X className="h-5 w-5" strokeWidth={2} />
            ) : (
              <Menu className="h-5 w-5" strokeWidth={2} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen ? (
        <div
          className="md:hidden border-t border-slate-200 bg-white"
          data-testid="nav-mobile-drawer"
        >
          <div className="max-w-7xl mx-auto px-5 py-3 flex flex-col gap-1">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`${l.testid}-m`}
                onClick={() => setMobileOpen(false)}
                className={`px-3 py-2.5 rounded-md text-sm ${
                  isActive(l.to)
                    ? "bg-slate-100 text-slate-900"
                    : "text-slate-700 hover:bg-slate-100"
                }`}
              >
                {l.label}
              </Link>
            ))}
            <div className="my-1 h-px bg-slate-200" />
            {user ? (
              <>
                <div className="px-3 py-1.5 text-xs font-mono text-slate-500 truncate">
                  {user.email}
                  {user.role === "admin" ? " · admin" : ""}
                </div>
                <button
                  type="button"
                  onClick={onSignOut}
                  data-testid="nav-signout-m"
                  className="text-left px-3 py-2.5 rounded-md text-sm text-slate-700 hover:bg-slate-100"
                >
                  Sign out
                </button>
              </>
            ) : (
              <Link
                to="/login"
                data-testid="nav-signin-m"
                onClick={() => setMobileOpen(false)}
                className="px-3 py-2.5 rounded-md text-sm cre-cta-primary text-center font-semibold"
              >
                Sign in
              </Link>
            )}
          </div>
        </div>
      ) : null}
    </header>
  );
}
